import { beautifyTheLowHighObject, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { CompareField, ComparePoint, CompareRankField } from './compare-config';

/**
 * @description 对比表格与综合数据排序共用的数值格式化、最优值判定方法
 */
export function useCompareValue() {
  const methods = useMethods({
    /**
     * @description 格式化对比指标值
     * @param value 指标原始值
     * @param field 指标配置
     * @return 带单位的展示值，缺失值统一展示 -
     */
    formatValue(value: any, field: CompareField) {
      if (!isNotEmpty(value)) return '-';
      if (field.kind === 'text') return String(value);
      const targetValue = Number(value);
      if (Number.isNaN(targetValue)) return String(value);
      // 销售额接口返回单位为元，展示时统一换算成万元并保留最多两位小数
      const displayValue = field.kind === 'sales' ? Number((targetValue / 10000).toFixed(2)) : targetValue;
      return `${String(displayValue)}${field.unit || ''}`;
    },
    /**
     * @description 格式化综合数据排序卡片的字段值
     * 金额类字段 >10000 转万，<=10000 展示原值；不展示“元”
     * @param value 字段原始值
     * @param field 排序卡片字段配置
     */
    formatRankValue(value: any, field: CompareRankField) {
      if (!isNotEmpty(value)) return '-';
      const targetValue = Number(value);
      if (Number.isNaN(targetValue)) return String(value);
      if (field.money) {
        if (targetValue > 10000) {
          const result = beautifyTheLowHighObject(1, targetValue, 2, { low: '', high: '万', top: '亿' });
          return `${result.val}${result.unit}`;
        }
        return String(targetValue);
      }
      return `${String(targetValue)}${field.unit || ''}`;
    },
    /**
     * @description 判断当前值是否为该指标的最优值
     * 缺失值、负数不参与判定；参与对比的点位少于 2 个时不判定；
     * 所有有效值相同（含仅一个有效值）时没有比较差异，不高亮；存在差异时并列最优均返回 true
     * @param points 参与对比的全部点位
     * @param key 指标字段
     * @param value 当前点位指标值
     * @param direction 最优方向
     */
    isBestValue(points: ComparePoint[], key: string, value: any, direction?: 'min' | 'max') {
      if (!direction || points.length < 2 || !isNotEmpty(value)) return false;
      const currentValue = Number(value);
      // 当前值为负数或非法数字时不参与最优高亮
      if (Number.isNaN(currentValue) || currentValue < 0) return false;
      const validValues = points
        .map((point) => point[key])
        .filter((item) => isNotEmpty(item))
        .map((item) => Number(item))
        // 负数不参与值的对比
        .filter((item) => !Number.isNaN(item) && item >= 0);
      if (!validValues.length) return false;
      const minValue = Math.min(...validValues);
      const maxValue = Math.max(...validValues);
      // 所有有效值相同（含只有一个有效值）说明没有比较差异，不做最优高亮
      if (minValue === maxValue) return false;
      const bestValue = direction === 'min' ? minValue : maxValue;
      return currentValue === bestValue;
    },
  });

  return methods;
}
